/**
 * Google タグマネージャー（GTM）連携ユーティリティ
 *
 * コンテナの読み込み自体は AnalyticsProvider が「拒否済みでないとき」にのみ行う（オプトアウト方式）。
 * 本モジュールは「コンテナ識別子の公開」「イベント名の一元定義」「dataLayer への push」だけを担う。
 *
 * dataLayer を新規生成しない設計にしている理由:
 * 拒否済みユーザーではタグスクリプトを読み込まないため dataLayer は存在せず、push は no-op になる。
 * ここで配列を生成すると拒否済みユーザーの行動がキューに溜まり、将来何らかのスクリプトが
 * 配列を処理した際にまとめて送信されるリスクがある。配列が既に存在する状態
 * （= いずれかのタグスクリプトが読み込まれた状態）でのみ push する。
 */

import { isTrackingRejected } from "./consent";

declare global {
  interface Window {
    // 省略可能で宣言する理由: non-optional にすると pushToDataLayer の未初期化チェックが
    // 型上は不要なコードに見え、将来削除されて拒否済みユーザーの queue 事故を招く。
    // 省略可能にしておくことで、チェックの必要性を型検査が保証する。
    dataLayer?: unknown[];
  }
}

export const GTM_CONTAINER_ID =
  process.env.NEXT_PUBLIC_GTM_CONTAINER_ID ?? "";

/**
 * GTM 管理画面のカスタムイベントトリガーと文字列一致させる必要があるイベント名。
 * リテラルを散らすと typo や改名で計測が無言で止まるため、ここに集約する。
 */
export const GTM_EVENTS = {
  /** SPA のページ遷移。GA4 予約語の page_view は避ける */
  spaPageView: "spa_page_view",
  /** 資料請求フォームの送信完了（企業向けの主要コンバージョン）*/
  bizDocumentRequest: "biz_document_request",
  /** 無料相談の予約完了 */
  bizMeetingBooked: "biz_meeting_booked",
} as const;

export function pushToDataLayer(
  eventName: string,
  params?: Record<string, unknown>
): void {
  // 拒否済みなら push しない: 拒否した瞬間のページでは読み込み済みの GTM が
  // 生き続けており、push が届くと広告タグが発火する（gtag.ts の event() 経由
  // 以外の将来の直接呼び出しにも効くよう、ここでも判定する）
  if (isTrackingRejected()) return;
  if (!GTM_CONTAINER_ID) return;
  if (typeof window === "undefined") return;
  // try/catch でラップする理由:
  // 1. 一部のadblockerが window.dataLayer を throw するゲッターに置き換える
  // 2. GTM は dataLayer.push 自体を差し替えるため、タグ内の例外がここまで伝播しうる
  // 計測は best-effort であり、失敗して画面を壊してはならない。
  try {
    const dataLayer = window.dataLayer;
    // 他スクリプトが配列以外で上書きしているケースも弾く
    if (!Array.isArray(dataLayer)) return;
    dataLayer.push({ event: eventName, ...params });
  } catch {
    // 計測失敗は握りつぶす（twq.ts と同方針）
  }
}
